import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { IssuePinItem } from "@ServerTypes/issues/issues.schema";

export type { IssuePinItem };

/**
 * What this store actually holds: the id it is keyed by and the pin placement. The wire row also
 * carries `isFormTemplateValid`, but that is read off the ISSUE (Issuecard/viewIssue), never from
 * here — and `mergeIssuesPinData` is also dispatched from the create/update response, which is not
 * a pin row at all. Declaring the subset keeps that call honest instead of inventing the flag.
 */
export type IssuePinEntry = Pick<IssuePinItem, "id" | "pinData">;

type IssuesPinDataState = Record<string, IssuePinEntry>;

/**
 * `/getIssuesPinData` is now gated by `issuePinRowSchema` — `{ id, pinData?, isFormTemplateValid }`,
 * the row it actually answers with — instead of `issueListResponse` (the full issue item). The
 * dispatch site still hands `response.data.result` over as `unknown`, so this guard stays as the
 * runtime boundary; it checks the `id` this state is keyed by.
 */
function isPinItem(value: unknown): value is IssuePinEntry {
  return typeof value === "object" && value !== null && "id" in value && typeof value.id === "string";
}

const issuesPinDataSlice = createSlice({
  name: "issuesPinData",
  initialState: {} as IssuesPinDataState,
  reducers: {
    setIssuesPinData: (_state: IssuesPinDataState, action: PayloadAction<unknown>) => {
      const rows: unknown[] = Array.isArray(action.payload) ? action.payload : [];
      return rows.reduce<IssuesPinDataState>(
        (prev, curr) => (isPinItem(curr) ? { ...prev, [curr.id]: curr } : prev),
        {},
      );
    },
    mergeIssuesPinData: (state: IssuesPinDataState, action: PayloadAction<IssuePinEntry>) => {
      state[action.payload.id] = { ...action.payload };
    },
    deleteIssuesPinData: (state: IssuesPinDataState, action: PayloadAction<string[]>) => {
      action.payload.forEach((issue: string) => {
        delete state[issue];
      });
    },
  },
});

export const { setIssuesPinData, mergeIssuesPinData, deleteIssuesPinData } = issuesPinDataSlice.actions;
export default issuesPinDataSlice.reducer;
